import { AbstractFormComponent } from '../abstract-form-component/abstract-form-component';
import { Range } from '../../models/range';
import { clamp } from '../../utils/clamp';
import { NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'tmx-range-slider',
  templateUrl: './range-slider.component.html',
  styleUrl: './range-slider.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RangeSliderComponent,
    },
  ],
  imports: [NgStyle, NgClass],
})
export class RangeSliderComponent extends AbstractFormComponent<Range> {
  private readonly cd = inject(ChangeDetectorRef);

  @HostBinding('class') cssClass = '';

  @ViewChild('track') track!: ElementRef<HTMLDivElement>;

  @Input() min = 0;

  @Input() max = 0;

  minThumbDown = signal(false);
  maxThumbDown = signal(false);

  selectedMinTrackWidth = signal({ width: '0%' });
  selectedMaxTrackWidth = signal({ width: '0%' });

  minThumbLeft = signal({ left: '0%' });
  maxThumbRight = signal({ right: '0%' });

  ignoreClick = signal(false);

  constructor() {
    super(new Range());

    effect(() => {
      this.cssClass = this.disabled() ? 'disabled' : '';
      this.cd.markForCheck();
    });
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (this.disabled()) {
      return;
    }

    if (this.minThumbDown()) {
      this.dragMinThumb(event);
    }

    if (this.maxThumbDown()) {
      this.dragMaxThumb(event);
    }
  }

  @HostListener('document:pointerup')
  onPointerUp() {
    if (!this.minThumbDown() && !this.maxThumbDown()) {
      return;
    }

    this.minThumbDown.set(false);
    this.maxThumbDown.set(false);
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.ignoreClick()) {
      this.ignoreClick.set(false);
      return;
    }

    this.markAsTouched();
    if (this.disabled()) {
      return;
    }

    const { min, max } = this.value();
    const value = clamp(
      this.convertPercentageToValue(this.convertEventToPercentage(event)),
      this.min,
      this.max
    );

    if (value <= min) {
      this.moveMinThumb(value);
    } else if (value >= max) {
      this.moveMaxThumb(value);
    } else if (value - min <= max - value) {
      this.moveMinThumb(value);
    } else {
      this.moveMaxThumb(value);
    }
  }

  override onWriteValue() {
    this.updateMinThumb(
      this.convertValueToMinThumbPercentage(this.value().min)
    );
    this.updateMaxThumb(
      this.convertValueToMaxThumbPercentage(this.value().max)
    );
  }

  onMinThumbKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab') {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        this.value.set(
          new Range(
            clamp(this.value().min + 1, this.min, this.value().max),
            this.value().max
          )
        );
        this.onChange(this.value());
        this.updateMinThumb(
          this.convertValueToMinThumbPercentage(this.value().min)
        );
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        this.value.set(
          new Range(
            clamp(this.value().min - 1, this.min, this.value().max),
            this.value().max
          )
        );
        this.onChange(this.value());
        this.updateMinThumb(
          this.convertValueToMinThumbPercentage(this.value().min)
        );
        break;
      // No default
    }
  }

  onMaxThumbKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab') {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        this.value.set(
          new Range(
            this.value().min,
            clamp(this.value().max + 1, this.value().min, this.max)
          )
        );
        this.onChange(this.value());
        this.updateMaxThumb(
          this.convertValueToMaxThumbPercentage(this.value().max)
        );
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        this.value.set(
          new Range(
            this.value().min,
            clamp(this.value().max - 1, this.value().min, this.max)
          )
        );
        this.onChange(this.value());
        this.updateMaxThumb(
          this.convertValueToMaxThumbPercentage(this.value().max)
        );
        break;
      // No default
    }
  }

  private dragMinThumb(event: MouseEvent) {
    const value = clamp(
      this.convertPercentageToValue(this.convertEventToPercentage(event)),
      this.min,
      this.value().max
    );
    const range = new Range(value, this.value().max);
    this.value.set(range);
    this.onChange(range);
    this.updateMinThumb(this.convertValueToMinThumbPercentage(value));
  }

  private dragMaxThumb(event: MouseEvent) {
    const value = clamp(
      this.convertPercentageToValue(this.convertEventToPercentage(event)),
      this.value().min,
      this.max
    );
    const range = new Range(this.value().min, value);
    this.value.set(range);
    this.onChange(range);
    this.updateMaxThumb(this.convertValueToMaxThumbPercentage(value));
  }

  private moveMinThumb(value: number) {
    const range = new Range(value, this.value().max);
    this.value.set(range);
    this.onChange(range);
    this.updateMinThumb(this.convertValueToMinThumbPercentage(value));
  }

  private moveMaxThumb(value: number) {
    const range = new Range(this.value().min, value);
    this.value.set(range);
    this.onChange(range);
    this.updateMaxThumb(this.convertValueToMaxThumbPercentage(value));
  }

  private updateMinThumb(percentage: number) {
    this.selectedMinTrackWidth.set({ width: `${percentage}%` });
    this.minThumbLeft.set({ left: `${percentage}%` });
  }

  private updateMaxThumb(percentage: number) {
    this.selectedMaxTrackWidth.set({ width: `${percentage}%` });
    this.maxThumbRight.set({ right: `${percentage}%` });
  }

  private convertEventToPercentage(event: MouseEvent) {
    const bounds = this.track.nativeElement.getBoundingClientRect();
    return Math.round(
      clamp((event.clientX - bounds.left) / bounds.width) * 100
    );
  }

  private convertValueToMinThumbPercentage(value: number) {
    return Math.round(clamp((value - this.min) / (this.max - this.min)) * 100);
  }

  private convertValueToMaxThumbPercentage(value: number) {
    return Math.round(clamp((this.max - value) / (this.max - this.min)) * 100);
  }

  private convertPercentageToValue(percentage: number) {
    return this.min + Math.round((percentage / 100) * (this.max - this.min));
  }
}
